import { reactive } from 'sitekit-extensions'
import wordsearch from 'wordsearch'
import destroys from '../util/elements/destroys'
import on from '../util/elements/on'
import {
  line,
  coordsMatch,
  distanceFromPoints,
  pageCoordFromEvent,
  Coord,
  setOrigin,
  numIsNan,
  directionFromPoints
} from '../util/math/graphing'
import getMax from '../util/array/get-max'
import { setMinMax } from '../util/math/set-min-max'

const { floor } = Math

export default function wordSearch (Site, $) {
  Site.widget('puzzle-wordsearch', {
    use: [reactive()],
    _create () {
      destroys(this)
      this.puzzleController = Site.findWidget('puzzle-controller')
      this.pageDrawer = Site.findWidget('page-draw')

      const answers = this.puzzleController.state.answers.map(x => x.toLowerCase().replace(/ /g, ''))

      this.currentSelection = []

      // Get the longest name
      // Then make sure it is bigger than 15
      // But no bigger than 20
      this.gridSize = setMinMax(
        getMax(answers.map(name => name.length)),
        15,
        20
      )

      this.resize(true)

      this.state = {
        size: this.rect.width / this.gridSize,
        wordsearch: getWordsearch(answers, this.gridSize)
      }

      // This controls how far off track the user can go when drawing a line
      this.errorBoundary = this.state.size / 1.5

      this.hookEvents()
    },
    hookEvents () {
      // We only use mouse events for the puzzle
      this.destroys(
        on(this.element, 'mousedown', e => this.dragStart(e)),
        on($(window), 'mouseup', e => this.dragEnd(e)),
        on(this.element, 'mousemove touchmove', e => this.move(e)),
        on($(window), 'resize', e => this.resize())
      )
    },
    _transitionIn () {
      this.resize()
      this.element.addClass('in')
    },
    dragStart (e) {
      e.preventDefault()
      this.startedHere = true
      this.startPos = this.coordFromEvent(e)
      this.currentSelection = []

      const elAtCoords = this.getElementFromCoords(this.startPos)

      this.currentSelection.push(elAtCoords)
    },
    move (e) {
      // Don't run conditions that cause an early exit
      if (!this.startPos || this.hasFailed) return

      const startPos = this.startPos
      const currentPos = this.coordFromEvent(e)

      // Exit early if the current mousemove is the same as the startPos
      if (coordsMatch(startPos, currentPos)) {
        return console.log('Got matching coords')
      }

      let closestPointOnLine

      // Form a line from the two positions that snaps to 45 degree intervals
      const curLine = line(
        startPos,
        currentPos
      ).snap(45)

      /*
        If we have yet to move 0.4 letters away from start and we haven't selected any more letters
        OR if this.theLine hasn't been set (could happen if you move your mouse really quickly)

        this.theLine should basically always be set. But it can be reset while you are within setLineDistance 
        and you have selected less than 2 letters
        */
      const setLineDistance = this.state.size * 2
      const isWithinSetLineDistance = distanceFromPoints(startPos, currentPos) < setLineDistance
      // const hasSelectedLessThan2 = this.currentSelection.length < 2
      // const theLineIsNotSet = !this.theLine

      if (isWithinSetLineDistance && !this.hasBeenSet) {
        this.theLine = curLine
        if (numIsNan(this.theLine.gradent)) {
          console.log('Got a nan gradient', this.theLine.gradient)
        }
      } else {
        // The previous this.theLine is now set
        this.hasBeenSet = true
      }

      if (this.theLine) {
        const currentDistanceFromLine = this.theLine.distanceFromPoint(currentPos)

        if (currentDistanceFromLine > this.errorBoundary) {
          // We have moved past the point of no return 
          return this.cancelSelection()
        } else {
          closestPointOnLine = this.theLine.closestPoint(currentPos)
        }
      } else {
        return
      }

      /* 
        Say a user has a cross word 'a b c d' 
        If the users starts a selection on 'b' and then moves to 'c'
        this stops them then going back and selecting 'a'
        */
      if (this.currentSelection.length > 1) {
        const doesNotPassDirectionTest = !this.directionTest(closestPointOnLine)
        if (doesNotPassDirectionTest) {
          return this.cancelSelection()
        }
      }

      const $el = this.getElementFromCoords(closestPointOnLine)

      // $el.addClass('toggled')
      this.currentSelection.push($el)
    },
    coordFromEvent (e) {
      return setOrigin(pageCoordFromEvent(e), this.offset)
    },
    getElementFromCoords (coord) {
      const elCoord = new Coord(
        floor(coord.x / this.state.size),
        floor(coord.y / this.state.size)
      )
      const el = this.element.find(`[data-xy="${elCoord.x}:${elCoord.y}"]`)
      return el
    },
    dragEnd () {
      if (!this.startedHere) return

      const wasCorrect = this.cancelSelection()

      if (!wasCorrect) {
        setTimeout(() => {
          this.pageDrawer.undo()
        }, 10)
      }

      this.startedHere = false
    },
    fillSelection (selection) {
      const subject = []

      selection.forEach((letterInfo, i) => {
        if (i === 0) {
          subject.push(letterInfo)
          return
        }

        let missingEl
        let last = subject[subject.length - 1]

        let tries = 0

        /**
         * Sometimes the selection returns [{x: 1}, {x: 2}, {x: 4}]
         * Which means we need to fill in the selection between x:2 and x:4 ({x: 3})
         * This do while loop does that
         */
        do {
          const { x, y } = letterInfo
          last = subject[subject.length - 1]
          let largeXDiff = Math.abs(x - last.x) > 1
          let largeYDiff = Math.abs(y - last.y) > 1

          missingEl = (this.theLine.isHorizontal)
            ? largeXDiff
            : (this.theLine.isVertical)
              ? largeYDiff
              : largeXDiff || largeXDiff

          if (missingEl) {
            let { x, y } = last
            const xDirection = directionFromPoints(selection[1].x, selection[0].x)
            const yDirection = directionFromPoints(selection[1].y, selection[0].y)

            if (this.theLine.isHorizontal) {
              x = x + 1 * -xDirection
            } else if (this.theLine.isVertical) {
              y = y + 1 * -yDirection
            } else {
              x = x + 1 * xDirection
              y = y + 1 * yDirection
            }
            const el = this.element.find(`[data-xy="${x}:${y}"]`)

            const data = this.letterInfoFromEl(el)

            if (!data) return []

            subject.push(data)
          }

          tries++
        } while (missingEl && tries < this.gridSize + 5)

        subject.push(letterInfo)
      })

      return subject
    },
    letterInfoFromEl (el) {
      const data = el.data()

      if (!data) {
        return false
      }
      const { xy, letter } = data
      const [x, y] = xy.split(':').map(Number)

      return {
        x, y, letter, xy, el
      }
    },
    getSelection () {
      let start
      let last
      let alreadyIn = []
      let onTheLine

      if (this.theLine) {
        if (this.theLine.isVertical) {
          onTheLine = el => el.x === start.x
        } else if (this.theLine.isHorizontal) {
          onTheLine = el => el.y === start.y
        } else {
          onTheLine = this.theLine.gradient > 0 ? el => start.y - start.x === el.y - el.x : el => start.y + start.x === el.y + el.x
        }

        const selection = []

        this.currentSelection.forEach((el, i) => {
          const letterInfo = this.letterInfoFromEl(el)
          const { xy, x, y } = letterInfo

          if (alreadyIn.includes(xy)) return

          if (i === 0) {
            alreadyIn.push(xy)
            selection.push(letterInfo)
            start = new Coord(x, y)
            last = start
            return
          }

          const result = onTheLine(letterInfo) && !coordsMatch(last, letterInfo)
          if (!result) return

          alreadyIn.push(xy)
          selection.push(letterInfo)
          last = letterInfo
        })

        const filledSelection = this.fillSelection(selection)

        const foundWord = filledSelection.map(x => x.letter).join('')

        this.currentSelection = filledSelection

        return foundWord
      }
    },
    cancelSelection (mouseUp = false) {
      if (!this.startPos) return

      const word = this.getSelection()
      let wasCorrect

      if (word) {
        wasCorrect = this.puzzleController.findWord(word)
        if (wasCorrect) {
          this.currentSelection.forEach(
            letter => letter.el.addClass('toggled')
          )
        }
      }

      this.startPos = false
      this.theLine = false
      this.hasFailed = false
      this.hasBeenSet = false
      this.currentSelection = []

      return wasCorrect
    },
    resize (quiet) {
      this.rect = this.element.get(0).getBoundingClientRect()
      const offset = this.element.offset()
      this.offset = new Coord(
        offset.left,
        offset.top
      )

      if (!quiet) {
        this.setState({
          size: this.rect.width / this.gridSize
        })
      }
    },
    react () {
      const extChange = this.puzzleController.changed('answers', prevState => {
        const state = this.puzzleController.state
        this.setState({
          wordsearch: getWordsearch(state.answers, this.gridSize)
        })
      })
      this.changed('wordsearch', prevState => {
        this.renderWordSearch(this.state.wordsearch)
        this.state.wordsearch.unplaced.forEach(
          word => this.puzzleController.findWord(word)
        )
      })
      this.changed('size', prevState => this.updateSize())

      this.destroys(extChange)
    },
    directionTest (a) {
      return ((this.currentSelection[0].x - this.currentSelection[1].x) > 0) === ((this.currentSelection[1].x - a.x) > 0) && ((this.currentSelection[0].x - this.currentSelection[1].x) > 0) === ((this.currentSelection[1].x - a.x) > 0)
    },
    updateSize () {
      this.errorBoundary = this.state.size * 2
      this.element.find('.puzzle-letter')
        .toArray()
        .forEach(el => {
          const $el = $(el)
          const { xy } = $el.data()
          const [x, y] = xy.split(':')

          $el.attr('style', `
            top: ${y * this.state.size}px;
            left: ${x * this.state.size}px;
            width: ${this.state.size / 2}px;
            height: ${this.state.size / 2}px;
            margin: ${this.state.size / 4}px;
          `)
        })
    },
    renderWordSearch (words) {
      this.element.empty()

      if (!words) return

      words.grid.forEach((row, y) => {
        row.forEach((letter, x) => {
          const $letter = $(`
          <span
          class="puzzle-letter"
          data-xy="${x}:${y}"
          data-letter="${letter}"
          style="
          top: ${y * this.state.size}px;
          left: ${x * this.state.size}px;
          width: ${this.state.size}px;
          height: ${this.state.size}px;
          "
          >${letter}</span>
          `)

          this.element.append($letter)
        })
      })
    }
  })
}

function getWordsearch (words, size) {
  if (!words.length) {
    return undefined
  }

  let bestSearch = false

  let tries = 0

  while (tries < 50) {
    tries++

    // The wordsearch needs there to be no spaces or uppercase letters
    const search = wordsearch(words, size, size)

    // If the search has no unplaced just return it immediately
    if (!search.unplaced.length) {
      console.log(`Wordsearch generation was successful on the ${tries} attempt, ${search.unplaced.length} unplaced`)
      return search
    }

    if (!bestSearch) {
      bestSearch = search
    } else if (search.unplaced.length < bestSearch.unplaced.length) {
      bestSearch = search
    }
  }

  console.log(`Tried to generate wordsearch ${tries} times, best search was missing ${bestSearch.unplaced.length}`)

  return bestSearch
}
